/**
 * 인앱 카메라 커스텀 훅
 *
 * getUserMedia Web API를 사용하여 <video> 엘리먼트에
 * 카메라 스트림을 렌더링합니다.
 */

import type React from "react";
import { useState, useCallback, useRef, useEffect } from "react";

// ============================================================================
// Types
// ============================================================================

export type FlashMode = "off" | "on";

export interface UseInAppCameraOptions {
  /** 에러 발생 콜백 */
  onError?: (error: Error) => void;
  /** 카메라 뷰파인더 영역 ref (핀치 줌 non-passive 터치 리스너용) */
  viewfinderRef?: React.RefObject<HTMLDivElement | null>;
}

export interface UseInAppCameraReturn {
  /** 카메라 사용 가능 여부 (getUserMedia 지원 여부) */
  isAvailable: boolean;
  /** 카메라 열림 상태 */
  isOpen: boolean;
  /** 카메라 프리뷰 준비 완료 */
  isCameraReady: boolean;
  /** 현재 플래시 모드 */
  flashMode: FlashMode;
  /** 연속 촬영 카운트 */
  captureCount: number;
  /** 현재 줌 레벨 (1.0 = 기본) */
  zoomLevel: number;
  /** 줌 인디케이터 표시 여부 (핀치 줌 시 true, 3초 후 false) */
  showZoomIndicator: boolean;
  /** video 엘리먼트 ref (InAppCamera.tsx에서 <video ref={camera.videoRef}> 로 연결) */
  videoRef: React.RefObject<HTMLVideoElement | null>;
  /** 카메라 열기 */
  open: () => Promise<void>;
  /** 카메라 닫기 */
  close: () => Promise<void>;
  /** 촬영 (base64 반환) */
  capture: () => Promise<string | null>;
  /** 전/후면 카메라 전환 */
  flipCamera: () => Promise<void>;
  /** 플래시 모드 토글 (off <-> on) */
  toggleFlash: () => void;
  /** 줌 레벨 설정 (CSS transform scale 기반 디지털 줌) */
  setZoom: (level: number) => void;
}

// 플래시 모드 순환 순서
const FLASH_CYCLE: FlashMode[] = ["off", "on"];

export function useInAppCamera(options: UseInAppCameraOptions = {}): UseInAppCameraReturn {
  const { onError, viewfinderRef } = options;

  // getUserMedia 지원 여부 (마운트 시 1회만 평가)
  const [isAvailable] = useState(() => !!navigator.mediaDevices?.getUserMedia);

  const [isOpen, setIsOpen] = useState(false);
  const [isCameraReady, setIsCameraReady] = useState(false);
  const [flashMode, setFlashMode] = useState<FlashMode>("off");
  const [captureCount, setCaptureCount] = useState(0);
  const [zoomLevel, setZoomLevel] = useState(1.0);
  const [showZoomIndicator, setShowZoomIndicator] = useState(false);

  // 내부 refs
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const facingModeRef = useRef<"environment" | "user">("environment");
  const isMountedRef = useRef(true);

  // 핀치 줌 추적
  const pinchStartDistRef = useRef(0);
  const pinchBaseZoomRef = useRef(1.0);
  const zoomIndicatorTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const zoomLevelRef = useRef(1.0);

  // zoomLevel 동기화
  useEffect(() => {
    zoomLevelRef.current = zoomLevel;
  }, [zoomLevel]);

  // 캔버스 초기화 (DOM에 추가하지 않음)
  useEffect(() => {
    canvasRef.current = document.createElement("canvas");
  }, []);

  // 언마운트 정리
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      // stream 정리
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      }
      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }
      // 줌 인디케이터 타이머 정리
      if (zoomIndicatorTimerRef.current) {
        clearTimeout(zoomIndicatorTimerRef.current);
      }
    };
  }, []);

  // stream의 video track에서 torch 적용 (내부 헬퍼)
  const applyTorchToTrack = useCallback((torchOn: boolean) => {
    const stream = streamRef.current;
    if (!stream) return;

    const track = stream.getVideoTracks()[0];
    if (!track) return;

    // torch는 비표준이나 대부분의 모바일 브라우저에서 지원 - capabilities 체크 없이 바로 적용
    track
      .applyConstraints({
        advanced: [{ torch: torchOn } as any], // eslint-disable-line @typescript-eslint/no-explicit-any
      })
      .catch(() => {
        // torch 미지원 기기에서 무시
      });
  }, []);

  // 카메라 열기
  const open = useCallback(async () => {
    if (!isAvailable) return;

    // 기존 stream 정리 (연속 호출 시 누수 방지)
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: facingModeRef.current,
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
        audio: false,
      });

      streamRef.current = stream;

      // video 엘리먼트에 연결
      const video = videoRef.current;
      if (video) {
        video.srcObject = stream;
        try {
          await video.play();
        } catch {
          // autoplay 정책 문제 시 muted 속성이 있으므로 대부분 성공
        }
      }

      // track.onended 처리: 외부에서 stream이 종료되면 자동 close
      const videoTrack = stream.getVideoTracks()[0];
      if (videoTrack) {
        videoTrack.onended = () => {
          if (isMountedRef.current) {
            setIsOpen(false);
            setIsCameraReady(false);
            setCaptureCount(0);
          }
          streamRef.current = null;
          if (videoRef.current) {
            videoRef.current.srcObject = null;
          }
        };
      }

      if (isMountedRef.current) {
        setIsOpen(true);
        setIsCameraReady(true);
        setCaptureCount(0);
        setZoomLevel(1.0);
      }
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      onError?.(err);
    }
  }, [isAvailable, onError]);

  // 카메라 닫기
  const close = useCallback(async () => {
    // stream 정리
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }

    // video 엘리먼트 정리
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }

    if (isMountedRef.current) {
      setIsOpen(false);
      setIsCameraReady(false);
      setCaptureCount(0);
      setZoomLevel(1.0);
    }
  }, []);

  // 촬영
  const capture = useCallback(async (): Promise<string | null> => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas || !streamRef.current) return null;

    try {
      // 캔버스를 비디오 실제 해상도에 맞춤
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      const ctx = canvas.getContext("2d");
      if (!ctx) return null;

      // 전면 카메라(user)이면 수평 반전하여 거울상으로 촬영
      if (facingModeRef.current === "user") {
        ctx.translate(canvas.width, 0);
        ctx.scale(-1, 1);
      }

      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

      // 변환 초기화 (다음 캡처를 위해)
      ctx.setTransform(1, 0, 0, 1, 0, 0);

      // toDataURL로 JPEG base64 생성
      const dataUrl = canvas.toDataURL("image/jpeg", 0.9);
      // data:image/jpeg;base64, prefix 제거하여 순수 base64만 반환
      const base64 = dataUrl.replace(/^data:image\/jpeg;base64,/, "");

      if (isMountedRef.current) {
        setCaptureCount((prev) => prev + 1);
      }

      return base64;
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      onError?.(err);
      return null;
    }
  }, [onError]);

  // 전/후면 카메라 전환
  const flipCamera = useCallback(async () => {
    if (!streamRef.current) return;

    try {
      // 현재 stream 중지
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;

      // facingMode 토글
      facingModeRef.current = facingModeRef.current === "environment" ? "user" : "environment";

      // 새 stream 시작
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: facingModeRef.current,
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
        audio: false,
      });

      streamRef.current = stream;

      const video = videoRef.current;
      if (video) {
        video.srcObject = stream;
        try {
          await video.play();
        } catch {
          // autoplay 정책 문제 무시
        }
      }

      // 새 track의 onended 등록
      const videoTrack = stream.getVideoTracks()[0];
      if (videoTrack) {
        videoTrack.onended = () => {
          if (isMountedRef.current) {
            setIsOpen(false);
            setIsCameraReady(false);
            setCaptureCount(0);
          }
          streamRef.current = null;
          if (videoRef.current) {
            videoRef.current.srcObject = null;
          }
        };
      }

      // 줌 리셋
      if (isMountedRef.current) {
        setZoomLevel(1.0);
      }
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      onError?.(err);
    }
  }, [onError]);

  // 플래시 모드 토글
  const toggleFlash = useCallback(() => {
    setFlashMode((prev) => {
      const currentIndex = FLASH_CYCLE.indexOf(prev);
      const nextMode = FLASH_CYCLE[(currentIndex + 1) % FLASH_CYCLE.length];

      // torch 적용 (on이면 true, 그 외 false)
      applyTorchToTrack(nextMode === "on");

      return nextMode;
    });
  }, [applyTorchToTrack]);

  // 줌 레벨 설정 (CSS transform scale 기반 디지털 줌)
  const setZoom = useCallback((level: number) => {
    const clamped = Math.min(5, Math.max(1, level));
    setZoomLevel(clamped);
  }, []);

  // 줌 인디케이터 표시 (3초 후 자동 숨김)
  const showZoomIndicatorBriefly = useCallback((level: number) => {
    if (!isMountedRef.current) return;
    setZoomLevel(level);
    setShowZoomIndicator(true);

    if (zoomIndicatorTimerRef.current) {
      clearTimeout(zoomIndicatorTimerRef.current);
    }
    zoomIndicatorTimerRef.current = setTimeout(() => {
      if (isMountedRef.current) {
        setShowZoomIndicator(false);
      }
    }, 3000);
  }, []);

  // 핀치 줌: non-passive 네이티브 DOM 리스너 등록
  useEffect(() => {
    const el = viewfinderRef?.current;
    if (!el || !isOpen) return;

    const getDistance = (touches: TouchList) => {
      const dx = touches[0].clientX - touches[1].clientX;
      const dy = touches[0].clientY - touches[1].clientY;
      return Math.sqrt(dx * dx + dy * dy);
    };

    const onTouchStart = (e: TouchEvent) => {
      if (e.touches.length === 2) {
        pinchStartDistRef.current = getDistance(e.touches);
        pinchBaseZoomRef.current = zoomLevelRef.current;
      }
    };

    const onTouchMove = (e: TouchEvent) => {
      if (e.touches.length !== 2 || pinchStartDistRef.current === 0) return;
      // non-passive 리스너이므로 preventDefault가 실제로 동작함
      e.preventDefault();

      const currentDist = getDistance(e.touches);
      const scale = currentDist / pinchStartDistRef.current;
      const newZoom = Math.min(5, Math.max(1, pinchBaseZoomRef.current * scale));
      const rounded = Math.round(newZoom * 10) / 10;

      // 실제 zoom constraint 적용
      setZoom(rounded);
      showZoomIndicatorBriefly(rounded);
    };

    const onTouchEnd = () => {
      pinchStartDistRef.current = 0;
    };

    el.addEventListener("touchstart", onTouchStart, { passive: true });
    el.addEventListener("touchmove", onTouchMove, { passive: false });
    el.addEventListener("touchend", onTouchEnd, { passive: true });

    return () => {
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchmove", onTouchMove);
      el.removeEventListener("touchend", onTouchEnd);
    };
  }, [isOpen, viewfinderRef, showZoomIndicatorBriefly, setZoom]);

  return {
    isAvailable,
    isOpen,
    isCameraReady,
    flashMode,
    captureCount,
    zoomLevel,
    showZoomIndicator,
    videoRef,
    open,
    close,
    capture,
    flipCamera,
    toggleFlash,
    setZoom,
  };
}
